import { PluginHelper } from "./Plugin";
import {BoundMethod, bufferToHex, splitEscaped} from "./util/CoreUtils";
import { bufferToBase64 } from './util/DomUtils';
import { app } from './boot';
import {PropertyObject} from "./lib";
import {idbGet, idbPut, openDb} from "./util/IDBUtils";
import {SearchableMenu} from "./DomMenu";

const AsyncFunction = eval("(async function () {}.constructor)") as FunctionConstructor;

function _isSyntaxCheckingSafe() {
    function check(...args: string[]) {
      try {
        Function.apply(null, arguments);
        return true;
      } catch (e) {
        if (!(e instanceof SyntaxError))
          return true;
      }
      return false;
    }
  
    /* this one first, as it does not outright crash buggy JavaScriptCore */
    if (check("/*", "*/){"))
      return false;
    if (check("},function(){"))
      return false;
    if (check("};{"))
      return false;
  
    /* no problems found, fingers crossed */
    return true;
}
export const isSyntaxCheckingSafe = _isSyntaxCheckingSafe();
if (!isSyntaxCheckingSafe) console.warn("Syntax checking might be unsafe in this JavaScript engine, disabling dynamic content!");

class TrustManager {
    indexeddb : IDBDatabase;
    private static _instance : Promise<TrustManager>;
    private constructor() {
    }
    private async initialize() {
        this.indexeddb = await openDb('scriptTrustCache', 1, function(e) {
            if (e.oldVersion < 1) {
                this.result.createObjectStore("code", { keyPath: ["type","id"] });
            }
        });
    }
    static async create() {
        const inst = new TrustManager();
        await inst.initialize();
        return inst;
    }
    public static async instance() {
        if (!TrustManager._instance) TrustManager._instance = TrustManager.create();
        return TrustManager._instance;
    }
    public async markTrusted(id: string, code: string) {
        idbPut(this.indexeddb, "code", {type: 'script', id, code, localTrust: true});
    }
    public async storeSigned(id: string, code: string, signature: Array<Array<Uint8Array>>) {
        idbPut(this.indexeddb, "code", {type: 'script', id, code, signature});
    }
    public async getTrustedVersion(id: string) {
        return idbGet(this.indexeddb, "code", ['script', id]);
    }

    static async hashScriptCode(script): Promise<string> {
        const msgBuffer = new TextEncoder().encode(script.code);
        return bufferToHex(await crypto.subtle.digest('SHA-256', msgBuffer));
    }

    static script_trust_sources = [
        async function (script: CodeBlock, signature: Array<Array<Uint8Array>> | undefined): Promise<string | boolean> {
            if (signature && app.bundleInfo.trustedCodesigners) {
                const { id, lang, attrs, code } = script;
                const wrappedData = app.crypto.wrapData('codesign', { id, lang, attrs, code });
                const trust = app.bundleInfo.trustedCodesigners.split(',');
                for(const [k, s] of (signature as unknown as Array<[Uint8Array, Uint8Array]>)) {
                    if (trust.indexOf(await bufferToBase64(k)) === -1) continue;
                    try {
                        await app.crypto.verifyUnwrap('codesign', [k, s, wrappedData]);
                        return 'SIGNED';
                    } catch (e) { /* ignore invalid sigs */ }
                }
            }
        },
        async function (script: CodeBlock, signature: Array<Array<Uint8Array>> | undefined): Promise<string | boolean> {
            const hash = await TrustManager.hashScriptCode(script);
            var allowList = (localStorage.getItem('scriptAllowList') ?? '').split(/,/);
            if (allowList.indexOf(hash) !== -1) return 'ALLOWLIST';
        },
    ];

    static async getScriptTrust(script: CodeBlock, signature: Array<Array<Uint8Array>> | undefined): Promise<string | boolean> {
        for(const source of TrustManager.script_trust_sources) {
            const result = await source(script, signature);
            if (result) return result;
        }
        return false;
    }
}
type CodeBlock = {
    lang: string, attrs: string[], code: string, id: string,
};
export class ScriptHost extends PluginHelper {
    scriptResult: any = null;
    contentDisposableStack: DisposableStack;
    stderr: null | { charout(str: string): void } = null;
    stdout: null | { charout(str: string): void } = null;

    get [Symbol.dispose]() { return this.contentDisposableStack; }
    constructor(public idPath: string,
                public text: string,
                public signature: Array<Array<Uint8Array>> | undefined,
                public contextObj: object,
                /*contextRefs: {[name: string]: object}, */
                public itemInfo: PropertyObject) {
        super();
        this.contentDisposableStack = new DisposableStack();
    }

    /**
     * returns the main script, i.e. all js/javascript blocks without any :tag
     */
    getExecutableScript(): CodeBlock | null {
        return this.getCodeBlockById('js:main');
    }
    getCodeBlockById(id: string): CodeBlock | null {
        const blocks = this.getAllCodeBlocks().filter(x => x.id === id);
        return blocks.length == 0 ? null : { id, lang: blocks[0].lang, attrs: blocks[0].attrs, code: blocks.reduce((p, q) => p + q.code, "") };
    }
    getAllCodeBlocks(): CodeBlock[] {
        return ScriptHost.getAllCodeBlocks(this.text);
    }
    static attrKey(attrs: string[], key: string, defaultVal?: string): string {
        const attr = attrs.find(a => a === key || a.startsWith(key + '='));
        if (attr) return attr.substring(key.length + 1);
        else return defaultVal;
    }
    static getAllCodeBlocks(text: string): CodeBlock[] {
        const out : CodeBlock[] = [];
        for(let [_, lang, attrs_str, code] of text.matchAll(/^```([a-z]+)?[ \t]*(?:[ \t]([^\n]+))?(\n.*?\n)^```$/msg)) {
            const attrs = splitEscaped(attrs_str);
            if (lang === 'javascript') lang = 'js';
            let id = lang === 'js' ? 'js:main' : '';
            if (attrs[0]?.startsWith(':')) id = lang + ':' + attrs[0].substring(1);
            out.push({ id, lang, attrs, code });
        }
        return out;
    }
    async isScriptTrusted(): Promise<string | boolean | null> {
        const script = this.getExecutableScript();
        if (!script) return null;
        return TrustManager.getScriptTrust(script, this.signature);
    }
    @BoundMethod()
    log(message, ...args) {
        if (this.stderr) this.stderr.charout("[log] "+message+(args ? "\t" + args.map(x => `${x}`).join("\t") : "")+"\n");
        app.log_('log', { script: this }, message, undefined, ...args);
    }
    @BoundMethod()
    logError(message, error) {
        if (this.stderr) this.stderr.charout("[ERR] "+message+(error ? `\t${error}` : '') + "\n");
        if (error) app.toast_("error", { script: this },`${message}`, 5000, [], error);
        else app.toast_("error", { script: this },`${message}`, 5000, []);
    }
    @BoundMethod()
    print(...args) {
        if (this.stdout) this.stdout.charout(args.map(x => `${x}`).join("\t"));
        else app.log_('stdout', { script: this }, args.map(x => `${x}`).join("\t"), undefined, ...args);
    }
    @BoundMethod()
    println(...args) {
        if (this.stdout) this.stdout.charout(args.map(x => `${x}`).join("\t") + "\n");
        else app.log_('stdout', { script: this }, args.map(x => `${x}`).join("\t"), undefined, ...args);
    }
    async evalScriptContents(param, param2, event, target?) {
        var script = this.getExecutableScript();
        if (!script) throw new Error("no valid script");

        if (!await TrustManager.getScriptTrust(script, this.signature)) {
            throw new Error(`refusing to run script from [${this.idPath}](?${this.idPath}) without allowlisting`);
        }
        const fn = this.createExecutableScriptFunction(script);
        this.scriptResult = fn.call(this.contextObj, param, param2, event, target, this.log, this.logError, this.print, this.println);
    }
    async getScriptResult() {
        try {
            if (this.scriptResult !== null) await this.scriptResult;
        } catch {
            this.scriptResult = null;
        }
        if (this.scriptResult === null) await this.evalScriptContents(null, null, null);
        return this.scriptResult;
    }
    createExecutableScriptFunction(script: CodeBlock) : Function {
        if (!isSyntaxCheckingSafe) throw "Buggy JavaScript engine detected";
        const wrappedCode = '"use strict";' + script.code + '//# sourceURL=' + location.origin + '/memo/' + this.idPath.replace('#', '/') + '.txt';
        const functionConstructor : any = script.attrs.includes('async') ? AsyncFunction : Function;
        return new functionConstructor('param', 'param2', 'event', 'target', 'log', 'logError', 'print', 'println', wrappedCode);
    }
    async markScriptTrusted() {
        const trust = await this.isScriptTrusted();
        if (trust === null) throw new Error("no valid script");
        if (trust === 'SIGNED') return; // already signed
        if (trust === 'ALLOWLIST' && !ScriptHost.isTrustedCodesigner()) return; // already allowlisted and signing not possible
        const script = this.getExecutableScript();
        if (!script) throw new Error("no valid script");
        try {
            const fn = this.createExecutableScriptFunction(script);
        }catch(ex) {
            console.error(ex);
            throw ex;
        }
        const hash = await TrustManager.hashScriptCode(script);
        var allowList = (localStorage.getItem('scriptAllowList') ?? '').split(/,/);
        if (allowList.indexOf(hash) === -1) {
            switch((await SearchableMenu.show(ScriptHost.isTrustedCodesigner() ? [{text: "No", icon: "🚫"}, {text: "Sign", icon: "📜"}, {text: "Allowlist", icon: "✔️"}] : [{text: "No", icon: "🚫"}, {text: "Allowlist", icon: "✔️"}], {
                title: "Trust this script?",
                forResponse: true,
                cancelResponse: {},
                message: `Do you want to allowlist this script (last modified ${new Date(this.itemInfo.mt as number)} by ${this.itemInfo.mu}, hash ${hash})?`,
            })).text) {
                case "Sign":
                    return await this.signScript();
                case "Allowlist":
                    allowList.unshift(hash);
                    localStorage.setItem('scriptAllowList', allowList.join(','));
                    return null;
            }
        }
    }
    static isTrustedCodesigner() {
        return app.bundleInfo.trustedCodesigners.indexOf(app.crypto.clientKeyPublicKeyString) !== -1;
    }
    async signScript() {
        if (!ScriptHost.isTrustedCodesigner()) {
            throw new Error("i'm not a trusted codesigner 😢");
        }

        // compare TypedArrays
        function typedArraysAreEqual(a, b) {
            if (a.byteLength !== b.byteLength) return false;
            return a.every((val, i) => val === b[i]);
        }

        const script = this.getExecutableScript();
        if (!script) throw new Error("no valid script");
        try {
            const fn = this.createExecutableScriptFunction(script);
        }catch(ex) {
            console.error(ex);
            throw ex;
        }
        const { id, lang, attrs, code } = script;
        const [key,signature,wrappeddata] = await app.crypto.signWithClientKey('codesign', { id, lang, attrs, code });
        let old_sig = this.signature || [];
        let new_sig = [ [ key, signature] ];
        for(const [k,s] of old_sig) {
            if (typedArraysAreEqual(k, key)) continue;
            try {
                await app.crypto.verifyUnwrap('codesign', [k, s, wrappeddata]);
                new_sig.push([k,s]);
            } catch (e) { console.log(e); /* remove invalid sigs from list */ }
        }
        return {sig: new_sig};
    }
    resetScript() {
        this.scriptResult = null;
        this.contentDisposableStack.dispose();
        this.contentDisposableStack = new DisposableStack();
    }

}

